import { createContext, use, useCallback, type ReactNode } from 'react'
import { ExternalToast, toast } from 'sonner'
import { getErrorMessage, type ErrorResponse } from '@/lib/siteFunctions'

interface ErrorContextType {
  showError: (error: any, title?: string, data?: ExternalToast) => void
  showSuccess: (message: string, title?: string, data?: ExternalToast) => void
  showWarning: (message: string, title?: string, data?: ExternalToast) => void
  showInfo: (message: string, title?: string, data?: ExternalToast) => void
}

const ErrorContext = createContext<ErrorContextType>(undefined!)

export const useError = (): ErrorContextType => {
  const context = use<ErrorContextType>(ErrorContext)
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider')
  }
  return context
}

interface ErrorProviderProps {
  children: ReactNode
}

export const ErrorProvider = ({ children }: ErrorProviderProps) => {
  const showError = useCallback(
    (error: any, title = 'Error', data?: ExternalToast) => {
      const errorResponse: ErrorResponse = getErrorMessage(error)

      if (Array.isArray(errorResponse.message)) {
        // Handle validation errors - show each error separately
        errorResponse.message.forEach((msg: Array<string>, index: number) => {
          toast.error(
            `${title}${errorResponse.message.length > 1 ? ` ${index + 1}` : ''}`,
            {
              description: typeof msg === 'string' ? msg : String(msg),
              duration: 30_000,
              ...data,
            }
          )
        })
      } else {
        toast.error(title, {
          description: String(errorResponse.message),
          duration: 30_000,
          ...data,
        })
      }
    },
    []
  )

  const showSuccess = useCallback(
    (message: string, title = 'Success', data?: ExternalToast) => {
      toast.success(title, {
        description: message,
        duration: 4_000,
        ...data,
      })
    },
    []
  )

  const showWarning = useCallback(
    (message: string, title = 'Warning', data?: ExternalToast) => {
      toast.warning(title, {
        description: message,
        duration: 10_000,
        ...data,
      })
    },
    []
  )

  const showInfo = useCallback(
    (message: string, title = 'Info', data?: ExternalToast) => {
      toast.info(title, {
        description: message,
        duration: 3_000,
        ...data,
      })
    },
    []
  )

  const contextValue: ErrorContextType = {
    showError,
    showSuccess,
    showWarning,
    showInfo,
  }

  return <ErrorContext value={contextValue}>{children}</ErrorContext>
}
